import * as React from 'react';

import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { TrailerForm } from './TrailerForm';

import { useTranslations } from '@/transportation/i18n/useTranslations';
import { useCreateTrailerMutation } from '~/modules/transportation/hooks/mutations/useCreateTrailerMutation';
import { useUpdateTrailerMutation } from '~/modules/transportation/hooks/mutations/useUpdateTrailerMutation';
import {
  $resetTrailerForm,
  $trailerForm,
  $updateTrailerForm,
} from '~/modules/transportation/state/trailerForm.localState';
import { TrailerFormState } from '~/modules/transportation/types';

interface TrailerDialogProps {
  trailer?: TrailerFormState & { id: string };
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const TrailerDialog = ({
  trailer,
  isOpen,
  onClose,
  onSuccess,
}: TrailerDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const { mutateAsync: createTrailer, isPending: isCreating } =
    useCreateTrailerMutation();
  const { mutateAsync: updateTrailer, isPending: isUpdating } =
    useUpdateTrailerMutation();

  const formData = useAtomValue($trailerForm);
  const resetForm = useSetAtom($resetTrailerForm);
  const updateForm = useSetAtom($updateTrailerForm);

  React.useEffect(() => {
    if (isOpen && trailer) {
      updateForm(trailer);
    }
  }, [isOpen, trailer, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      if (trailer) {
        await updateTrailer({
          id: trailer.id,
          ...formData,
        });
        toast.success(t.trailersView.messages.updateSuccess);
      } else {
        await createTrailer(formData);
        toast.success(t.trailersView.messages.createSuccess);
      }
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.trailersView.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@TrailerDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {trailer
              ? t.trailersView.dialog.title.edit
              : t.trailersView.dialog.title.create}
          </div>
        </DialogHeader>

        <TrailerForm isEditing={!!trailer} />

        <DialogFooter>
          <Button
            color="failure"
            variant="outline"
            isPending={isCreating || isUpdating}
            onPress={handleClose}
          >
            {t.trailersView.dialog.actions.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            isPending={isCreating || isUpdating}
            isLoading={isCreating || isUpdating}
            onPress={handleSubmit}
          >
            {trailer
              ? t.trailersView.dialog.actions.save
              : t.trailersView.dialog.actions.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
